import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import { Button } from '@libero/core/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@libero/core/components/ui/dialog';
import BereitsBestellt from './BereitsBestellt';
import PlentyUebersicht from './PlentyUebersicht';
import {
  bestaetigungsZeilen,
  empfaengerErgebnisText,
  istDoppelstart,
  konversationText,
  laufStatusAnzeige,
  laufUmleitungText,
  versandHinweis,
  wegText,
  mailStatusAnzeige,
  schreibAuftragsZeilen,
  mailsGesendet,
  schreibZusammenfassung,
  schreibVorschauText,
  startDaten,
  testlaufBestaetigung,
  testlaufSchreibListe,
  TESTLAUF_KLASSE,
} from '../lauf';
import type { LaufErgebnis, Vorschau } from '../typen';

interface Props {
  vorschau: Vorschau | null;
  onLaufFertig?: () => void;
  /** Meldet, ob gerade ein Lauf startet oder läuft (MainPage sperrt dann die Lauf-Auswahl, WR-03). */
  onLaeuftAenderung?: (laeuft: boolean) => void;
}

/**
 * Start-Knopf, Bestaetigungsdialog, Fortschritt und Ergebnis des Laufs.
 * Der Start laeuft ueber einen eigenen api.post (der Hook-Start verschluckt den HTTP-Status),
 * danach verfolgt der Core-Hook useJobStatus den Core-Job — kein eigenes Polling.
 * Alle Texte aus dem Backend werden als React-Textknoten gerendert.
 */
export default function StartBereich({ vorschau, onLaufFertig, onLaeuftAenderung }: Props) {
  const [dialogOffen, setDialogOffen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [startFehler, setStartFehler] = useState<string | null>(null);
  const [startet, setStartet] = useState(false);
  const { status, isRunning, progress, result, error } = useJobStatus(jobId);

  const fertig = status === 'completed' || status === 'failed';
  const laeuft = isRunning || startet;
  useEffect(() => {
    onLaeuftAenderung?.(laeuft);
    return () => onLaeuftAenderung?.(false);
  }, [laeuft, onLaeuftAenderung]);
  const gemeldet = useRef<string | null>(null);
  useEffect(() => {
    if (fertig && jobId && gemeldet.current !== jobId) {
      gemeldet.current = jobId;
      onLaufFertig?.();
    }
  }, [fertig, jobId, onLaufFertig]);

  const zeilen = vorschau ? bestaetigungsZeilen(vorschau) : [];
  const schreiben = schreibVorschauText(vorschau?.schreibVorschau);
  const auftragsZeilen = schreibAuftragsZeilen(vorschau?.schreibVorschau);
  const ergebnis = fertig && result ? (result as unknown as LaufErgebnis) : null;
  const hinweis = versandHinweis(vorschau?.versand);
  const testlaufListe = testlaufSchreibListe(vorschau);
  const bestaetigung = testlaufBestaetigung(vorschau);
  const gesperrt = !vorschau || isRunning || startet || !!vorschau.versand?.gesperrt;

  const starten = async () => {
    if (!vorschau) return;
    setDialogOffen(false);
    setStartet(true);
    setStartFehler(null);
    try {
      // WR-01: Testlauf schickt die im Dialog angezeigten Aufträge mit; nur diese werden bestellt und geschrieben.
      // WR-03: gestartet wird der Lauf der angezeigten Vorschau, nicht die aktuelle Auswahl im Select.
      const res = await api.post('/programmes/daily-order/starten', startDaten(vorschau.lauf.id, vorschau));
      setJobId(res.data.data.jobId ?? null);
    } catch (err: any) {
      if (istDoppelstart(err)) {
        toast.error('Job läuft bereits');
        setStartFehler('Job läuft bereits');
      } else {
        setStartFehler(err?.response?.data?.error?.message ?? 'Lauf konnte nicht gestartet werden');
      }
    } finally {
      setStartet(false);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={() => setDialogOffen(true)}
          disabled={gesperrt}
          className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
        >
          {isRunning || startet ? 'Lauf läuft...' : 'Lauf starten'}
        </Button>
        {vorschau && (
          <span
            className={
              schreiben.gesperrt
                ? 'text-xs px-2 py-1 rounded bg-yellow-50 text-yellow-900 border border-yellow-300'
                : 'text-xs text-gray-600'
            }
          >
            {schreiben.zeile}
            {schreiben.hinweise.length > 0 && ` — ${schreiben.hinweise[0]}`}
          </span>
        )}
      </div>

      {hinweis && <div className={`p-3 rounded text-sm ${hinweis.klasse}`}>{hinweis.text}</div>}

      {startFehler && <div className="p-3 rounded bg-red-50 text-red-800 text-sm">{startFehler}</div>}

      <Dialog open={dialogOffen} onOpenChange={setDialogOffen}>
        {/* Höhe begrenzt: der Inhalt scrollt, Titel und Knöpfe bleiben sichtbar. */}
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {vorschau?.versand?.testlauf ? 'TESTLAUF starten' : 'Lauf starten'} — Referenz {vorschau?.referenz.text}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            {hinweis && <div className={`p-3 rounded text-sm ${hinweis.klasse}`}>{hinweis.text}</div>}
            {testlaufListe && (
              <section className={`p-3 rounded text-sm space-y-1 ${TESTLAUF_KLASSE}`}>
                {bestaetigung && <p>{bestaetigung.text}</p>}
                <h3>{testlaufListe.titel}</h3>
                {testlaufListe.grund && <p className="font-normal">{testlaufListe.grund}</p>}
                {testlaufListe.auftraege.length > 0 && (
                  <p className="font-normal">Würden geschrieben: Auftrag {testlaufListe.auftraege.join(', ')}</p>
                )}
              </section>
            )}
            {vorschau && !vorschau.versand?.testlauf && (
              <p className="text-sm p-2 rounded bg-[#DAD9D9]/40 text-[#1A1A1A]">
                Bestellt werden nur die Aufträge dieser Vorschau
                {vorschau.bestellteAuftragsIds.length > 0 && `: ${vorschau.bestellteAuftragsIds.join(', ')}`}. Was nach dem
                Laden der Vorschau in den Status kommt, bleibt für den nächsten Lauf liegen.
              </p>
            )}
            <table className="w-full text-sm border border-[#DAD9D9]">
              <thead className="bg-[#DAD9D9] text-[#1A1A1A]">
                <tr>
                  <th className="px-3 py-2 text-left">Lieferant</th>
                  <th className="px-3 py-2 text-left">Weg</th>
                  <th className="px-3 py-2 text-left">Empfänger</th>
                  <th className="px-3 py-2 text-right">Positionen</th>
                  <th className="px-3 py-2 text-right">Aufträge</th>
                  <th className="px-3 py-2 text-left">Mail</th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z) => (
                  <tr key={z.id} className="border-t border-[#DAD9D9]">
                    <td className="px-3 py-2">{z.name}</td>
                    <td className="px-3 py-2">{z.mitMail ? wegText(z.weg) : '—'}</td>
                    <td className="px-3 py-2">{z.mitMail ? z.an : '—'}</td>
                    <td className="px-3 py-2 text-right">{z.positionen}</td>
                    <td className="px-3 py-2 text-right">{z.auftraege}</td>
                    <td className="px-3 py-2">{z.mitMail ? 'ja' : 'keine Mail'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {vorschau && <BereitsBestellt vorschau={vorschau} />}
            <section className="space-y-1 text-sm">
              <h3 className="font-semibold text-[#1A1A1A]">Plenty (nach dem Versand)</h3>
              <p className={schreiben.gesperrt ? 'p-2 rounded bg-yellow-50 text-yellow-900' : ''}>{schreiben.zeile}</p>
              {schreiben.hinweise.map((h) => (
                <p key={h} className="text-xs p-2 rounded bg-yellow-50 text-yellow-900">
                  {h}
                </p>
              ))}
              {auftragsZeilen.length > 0 && (
                <details>
                  <summary className="cursor-pointer">Aufträge und Soll-Tags ({auftragsZeilen.length})</summary>
                  <ul className="list-disc pl-5 text-xs max-h-40 overflow-auto">
                    {auftragsZeilen.map((z) => (
                      <li key={z.orderId}>{z.text}</li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
            <p className="text-xs text-gray-600">
              Der Lauf liest die Aufträge beim Start neu, die Zahlen können sich leicht ändern.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOffen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={starten}
              disabled={bestaetigung !== null && !bestaetigung.startErlaubt}
              className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
            >
              {schreiben.gesperrt ? 'Jetzt senden (Status wird nicht gesetzt)' : 'Jetzt senden und Status setzen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isRunning && (
        <div>
          <div className="h-2 bg-[#DAD9D9] rounded">
            <div className="h-2 bg-[#E9FF57] rounded" style={{ width: `${progress?.percentage ?? 0}%` }} />
          </div>
          <p className="text-sm mt-1">{progress?.message ?? 'Lauf startet...'}</p>
        </div>
      )}

      {fertig && !ergebnis && (
        <div className="p-3 rounded bg-red-50 text-red-800 text-sm">
          {error ?? 'Lauf fehlgeschlagen'}
        </div>
      )}

      {ergebnis && (
        <div className="space-y-2 border border-[#DAD9D9] rounded p-3">
          <div className={`px-3 py-2 rounded text-sm font-medium ${laufStatusAnzeige(ergebnis.status).klasse}`}>
            {laufStatusAnzeige(ergebnis.status).text} — Referenz {ergebnis.referenz}
          </div>
          {ergebnis.protokollFehler && (
            <div className="p-2 rounded bg-red-50 text-red-800 text-sm">{ergebnis.protokollFehler}</div>
          )}
          {laufUmleitungText(ergebnis) && (
            <div className="p-2 rounded bg-yellow-50 text-yellow-900 text-sm">{laufUmleitungText(ergebnis)}</div>
          )}
          <p className="text-xs text-gray-600">{ergebnis.absender.hinweis}</p>
          {(ergebnis.warnungen ?? []).length > 0 && (
            <details open={(ergebnis.warnungen ?? []).length <= 5} className="text-sm">
              <summary className="cursor-pointer">Warnungen ({(ergebnis.warnungen ?? []).length})</summary>
              <div className="space-y-1 mt-1">
                {(ergebnis.warnungen ?? []).map((w, i) => (
                  <div key={i} className="p-2 rounded bg-yellow-50 text-yellow-900">
                    {w}
                  </div>
                ))}
              </div>
            </details>
          )}
          <table className="w-full text-sm border border-[#DAD9D9]">
            <thead className="bg-[#DAD9D9] text-[#1A1A1A]">
              <tr>
                <th className="px-3 py-2 text-left">Lieferant</th>
                <th className="px-3 py-2 text-left">Empfänger</th>
                <th className="px-3 py-2 text-left">Datei</th>
                <th className="px-3 py-2 text-right">Positionen</th>
                <th className="px-3 py-2 text-left">FreeScout</th>
                <th className="px-3 py-2 text-left">Mail</th>
                <th className="px-3 py-2 text-left">Fehler</th>
              </tr>
            </thead>
            <tbody>
              {ergebnis.lieferanten.map((l) => {
                const anzeige = mailStatusAnzeige(l.mailStatus);
                return (
                  <tr key={l.id} className="border-t border-[#DAD9D9]">
                    <td className="px-3 py-2">{l.name}</td>
                    <td className="px-3 py-2">{empfaengerErgebnisText(l, ergebnis.testmodus)}</td>
                    <td className="px-3 py-2">{l.dateiname ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{l.positionen}</td>
                    <td className="px-3 py-2">{konversationText(l)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${anzeige.klasse}`}>{anzeige.text}</span>
                    </td>
                    <td className="px-3 py-2 text-red-800">{l.fehler ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PlentyUebersicht
            schreiben={ergebnis.schreiben}
            z={schreibZusammenfassung(
              ergebnis.schreiben,
              mailsGesendet(ergebnis),
              ergebnis.quellStatus ?? (ergebnis.testlauf === true ? 3.4 : 5.2),
            )}
          />
        </div>
      )}
    </section>
  );
}
